import { api } from './index';
import type { Vehicle } from './vehicles';
import type { Provider } from './provedor';

export type MaintenanceStatus = 'pending' | 'active' | 'rejected' | 'concluded';

export interface Maintenance {
  id: number;
  vehicle_id: number;
  provider_id: number;
  reason: string;
  entry_date: string;
  estimated_completion: string;
  final_price: number;
  status: MaintenanceStatus;
  created_at: string;
  updated_at: string;
  vehicle?: Vehicle;
  provider?: Provider;
  created_by_user?: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
}

export type CreateMaintenanceData = Omit<
  Maintenance,
  'id' | 'created_at' | 'updated_at' | 'status' | 'vehicle' | 'provider' | 'created_by_user'
>;
export type UpdateMaintenanceData = Partial<CreateMaintenanceData>;

const baseUrl = '/maintenance';
const compareByNewestUpdate = (left: Maintenance, right: Maintenance) =>
  new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime();

export const maintenanceService = {
  async getAll(status?: string): Promise<Maintenance[]> {
    const response = await api.get(baseUrl, { params: { status } });
    return response.data;
  },

  async getByStatuses(statuses: MaintenanceStatus[]): Promise<Maintenance[]> {
    const results = await Promise.all(statuses.map((status) => this.getAll(status)));
    return results.flat().sort(compareByNewestUpdate);
  },

  async getOpen(): Promise<Maintenance[]> {
    return this.getByStatuses(['pending', 'active']);
  },

  async getHistorical(): Promise<Maintenance[]> {
    return this.getByStatuses(['concluded', 'rejected']);
  },

  async create(data: CreateMaintenanceData): Promise<Maintenance> {
    const response = await api.post(baseUrl, data);
    return response.data;
  },

  async update(id: number, data: UpdateMaintenanceData): Promise<Maintenance> {
    const response = await api.put(`${baseUrl}/${id}`, data);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`${baseUrl}/${id}`);
  },

  async conclude(id: number): Promise<Maintenance> {
    const response = await api.patch(`${baseUrl}/${id}/conclude`);
    return response.data;
  },

  async approve(id: number): Promise<Maintenance> {
    const response = await api.patch(`${baseUrl}/${id}/approve`);
    return response.data;
  },

  async reject(id: number, reason?: string): Promise<Maintenance> {
    const response = await api.patch(`${baseUrl}/${id}/reject`, { reason });
    return response.data;
  },
};
